import { Location } from 'react-router-dom';
import { BookingWizardTab, SidebarStatus, VisibilityStatus } from '../BookingWizard';
import { appConfigStore } from 'Models/AppConfigStore';
import { combineAllPassengerTickets, validatePassengers } from './Passengers/PassengerTabHelpers';
import { PassengersTab } from './Passengers/PassengersTab';
import { SelectedTrips } from '../BookingWizardWrap';
import { getAddOnsTabVisibility, getExistingAddOnIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import AddOnsTab from './AddOn/AddOnTab';
import { BookingWizardData, BookingWizardInnerDataType } from '../BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from '../BookingWizardData/PassengerBookingWizardData';
import { isNotNullOrUndefined, stringIsEmpty } from 'Util/TypeGuards';
import { checkIfInvalidName } from 'Util/StringUtils';
import { WizardErrors } from '../BookingWizardTypes';
import BookingDetailsTab from './BookingDetails/BookingDetailsTab';
import { SeatingTab } from './Seating/SeatingTab';
import {
	getSeatingTabVisibility,
	returningSeatingOutstanding,
	unseatedFreeSeatPassengers,
	unseatedFreeSeatSignature,
	unseatedFreeSeatWarned,
	validateSeating,
} from './Seating/seatingTabGating';
import { useSeatingStore } from 'Stores/SeatingStore';
import { useDynamicBookingFieldsStore, getVisibleDynamicFields } from 'Stores/DynamicBookingFieldsStore';
import { validateDynamicFields } from './BookingDetails/DynamicFieldsSection';
import {
	canNavigateToCartStep,
	canNavigateToPaymentStep,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { BookingWizardTabNames } from '../BookingWizardConstants';
import { getCenteredPageContentClass } from './BulkBooking/utils';

export function getPassengerBookingTabs(
	wizardData: BookingWizardData,
	setWizardData: (bookingWizardData: BookingWizardData) => void,
	setBaseWizardErrors: (errors: WizardErrors<BookingWizardData>) => void,
	setInnerWizardErrors: (errors: WizardErrors<BookingWizardInnerDataType>) => void,
	selectedTrips: SelectedTrips,
	location: Location,
	oldWizardData?: BookingWizardData | null,
	// The booking subject's customer type (selected customer for staff-on-behalf) —
	// resolved reactively by the caller so the tabs rebuild when it lands.
	subjectUserDisplayNameId: string | null = null,
): BookingWizardTab[] {
	return [
		{
			name: BookingWizardTabNames.Passengers,
			displayName: 'Passengers',
			path: 'passengers',
			tabTitle: 'Passengers',
			tabTitleEdit: 'Edit Passengers',
			visibility: appConfigStore.showPassengerTab(wizardData)
				? VisibilityStatus.VISIBLE
				: VisibilityStatus.EXCLUDED,
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newBaseErrors: WizardErrors<BookingWizardData> = {};
				const newInnerErrors: WizardErrors<PassengerBookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					if (updateErrorState) {
						setBaseWizardErrors(newBaseErrors);
					}
					return true;
				}

				const errors = validatePassengers(combineAllPassengerTickets(wizardData));
				if (isNotNullOrUndefined(errors) && errors.length > 0) {
					newInnerErrors.adultTickets = 'Required field';
				}

				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				return Object.keys(newInnerErrors).length === 0 && Object.keys(newBaseErrors).length === 0;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <PassengersTab />,
			authorize: true,
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.Seating,
			displayName: 'Seats',
			path: 'seating',
			tabTitle: 'Seats',
			tabTitleEdit: 'Edit seats',
			// Excluded unless the selected trip's ferry has at least one class or one seat, so every
			// client that has not configured either keeps today's wizard exactly.
			visibility: getSeatingTabVisibility(wizardData),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newInnerErrors: WizardErrors<PassengerBookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					return true;
				}

				/*
					On a return booking, the first `Continue` moves to the homeward leg rather than
					out of the step.

					The step used to carry its own `Continue to return seating` button, directly
					above the wizard's, which asked the booker to notice that the obvious button was
					the wrong one. Refusing with "Choose a fare type for every passenger" instead is
					worse again — the passengers it means are on a sailing that is not on screen.

					Only while standing on the outbound leg, and only while the homeward one still
					has something to choose: with nothing outstanding there is nothing to send the
					booker back for, and from the homeward leg the error below is about what they
					are looking at.
				*/
				const seating = useSeatingStore.getState();
				if (updateErrorState && seating.leg === 'departing' && returningSeatingOutstanding(wizardData)) {
					seating.setLeg('returning');
					return false;
				}

				const error = validateSeating(wizardData);
				if (error) {
					(newInnerErrors as any).seating = error;
				}

				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				if (Object.keys(newInnerErrors).length > 0) {
					return false;
				}

				/*
					A warning, not an error: once, on the first `Continue`, where somebody could have
					chosen a seat for nothing and has not. The second press goes through, so a seat
					stays optional. Only when the booker pressed the button — the wizard also asks
					this step whether it is complete, and a warning is no reason to answer no.
				*/
				if (updateErrorState) {
					const unseated = unseatedFreeSeatPassengers(wizardData);
					if (!unseatedFreeSeatWarned(unseated, seating.seatWarningFor)) {
						seating.setSeatWarningFor(unseatedFreeSeatSignature(unseated));
						return false;
					}
				}
				return true;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <SeatingTab />,
			authorize: true,
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.BookingDetails,
			displayName: 'Details',
			path: 'details',
			tabTitle: 'Details',
			tabTitleEdit: 'Edit details',
			// Visible whenever at least one dynamic field for PASSENGER_ONLY exists for
			// the booking subject, the collect-primary-name app config is on, or the
			// passenger note ("Additional information") is enabled. Hidden otherwise so
			// the wizard stays unchanged for sites that haven't configured the feature.
			visibility: (() => {
				// When passenger info is collected, the primary name moves onto a passenger, so it no
				// longer keeps this tab visible on its own (only dynamic fields / the note do).
				if (appConfigStore.primaryBookingNameEnabled && !appConfigStore.passengerInfoEnabled)
					return VisibilityStatus.VISIBLE;
				if (appConfigStore.passengerNoteEnabled) return VisibilityStatus.VISIBLE;
				const fields = useDynamicBookingFieldsStore.getState().fields;
				return getVisibleDynamicFields(fields, 'PASSENGER_ONLY', subjectUserDisplayNameId).length > 0
					? VisibilityStatus.VISIBLE
					: VisibilityStatus.EXCLUDED;
			})(),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newInnerErrors: WizardErrors<PassengerBookingWizardData> = {};
				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					return true;
				}
				const inner = wizardData.innerWizardData as PassengerBookingWizardData;
				// Only validate the primary name here when it's collected on this tab. When passenger
				// info is enabled it's collected on (and validated against) a passenger instead.
				if (appConfigStore.primaryBookingNameEnabled && !appConfigStore.passengerInfoEnabled) {
					if (appConfigStore.primaryBookingFirstNameEnabled) {
						if (stringIsEmpty(inner?.primaryFirstName)) {
							(newInnerErrors as any).primaryFirstName = 'Required field';
						} else if (checkIfInvalidName(inner?.primaryFirstName)) {
							(newInnerErrors as any).primaryFirstName = "No spaces. A-Z and '-' only.";
						}
					}
					if (appConfigStore.primaryBookingLastNameEnabled) {
						if (stringIsEmpty(inner?.primaryLastName)) {
							(newInnerErrors as any).primaryLastName = 'Required field';
						} else if (checkIfInvalidName(inner?.primaryLastName)) {
							(newInnerErrors as any).primaryLastName = "No spaces. A-Z and '-' only.";
						}
					}
				}
				const fields = useDynamicBookingFieldsStore.getState().fields;
				const dynamicErrors = validateDynamicFields(
					fields,
					'PASSENGER_ONLY',
					subjectUserDisplayNameId,
					inner?.dynamicFieldValues,
				);
				if (Object.keys(dynamicErrors).length > 0) {
					(newInnerErrors as any).dynamicFieldValues = dynamicErrors;
				}
				// The "Additional information" note now lives in this step's section.
				if (
					appConfigStore.passengerNoteEnabled &&
					appConfigStore.passengerNoteRequired &&
					stringIsEmpty(inner?.note)
				) {
					(newInnerErrors as any).note = 'Required field';
				}
				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				return Object.keys(newInnerErrors).length === 0;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: <BookingDetailsTab />,
			authorize: true,
			showTAndCsButton: false,
		},
		{
			name: BookingWizardTabNames.AddOns,
			displayName: 'Add ons',
			path: 'add-ons',
			tabTitle: 'Add-ons',
			tabTitleEdit: 'Edit add-ons',
			visibility: getAddOnsTabVisibility(
				wizardData.tabSelected,
				selectedTrips,
				getExistingAddOnIds(wizardData, oldWizardData),
			),
			sidebarStatus: wizardData.isBulkBookingFlow ? SidebarStatus.NO_SIDEBAR : SidebarStatus.CART_SUMMARY,
			validateStep: updateErrorState => {
				const newInnerErrors: WizardErrors<PassengerBookingWizardData> = {};

				if (canNavigateToCartStep(location) || canNavigateToPaymentStep(location)) {
					return true;
				}

				if (updateErrorState) {
					setInnerWizardErrors(newInnerErrors);
				}
				return Object.keys(newInnerErrors).length === 0;
			},
			contentClass: wizardData.isBulkBookingFlow ? getCenteredPageContentClass() : undefined,
			component: (
				<>
					<AddOnsTab selectedTrips={selectedTrips} bookingType="passenger" />
				</>
			),
			authorize: true,
			showTAndCsButton: false,
		},
	];
}
